import copy from 'clipboard-copy'
import { batch } from 'react-redux'

import {
  createSharingLink,
  deleteSharingLink,
  fetchRecents,
  fetchRecordingPresignedUrl,
  fetchSessionAI,
  fetchSessionChat,
  fetchSessionPolls,
  fetchRecordingShareLink,
  fetchRecent,
  fetchSharingStatus
} from '~/services/recents'
import {
  ACTION_TYPES,
  DIALOGS,
  FILENAME_LINK_TYPES,
  HARDCODED_LINK_TYPES,
  LINK_MAPPINGS,
  LINK_TYPES,
  MAX_RECENTS_RESULTS,
  URL_REGEX
} from '~/core/constants'
import { logInfo } from '~/core/logger'
import { showErrorNotification, showInlineNotification } from '~/core/ui'
import { formatTranscriptTimestamp } from '~/core/utils/dates'
import api from '~/services/api'
import analytics from '~/services/analytics'
import { downloadAsHtml, downloadAsText, fetchSessionTranscript } from '~/services/transcript'
import { setDialog } from '~/actions/ui'
import { translate } from '~/core/translate'
import { getFetchingInProgress, getHasMore, getNextPageStartId, getSession } from '~/selectors/recents'
import deepmerge from 'deepmerge'
import { getIsMobileSummaryPage, isSalesforceIntegration } from '~/selectors/app'
import { getSessionParticipants, getSessionTranscript } from '~/selectors/session-details'

const { RECENTS } = ACTION_TYPES
const HEADER_SEPARATOR = '**************'

const formatDateComponents = (date) => {
  const meetingDate = new Date(date)
  const year = meetingDate.getFullYear()
  const month = String(meetingDate.getMonth() + 1).padStart(2, '0')
  const day = String(meetingDate.getDate()).padStart(2, '0')

  return { year, month, day }
}

const generateTranscriptHeader = (session, participants) => {
  const participantNames = participants.map(p => p.name).join('\n')
  const { year, month, day } = formatDateComponents(session.date)
  const formattedDate = `${year}.${month}.${day}`

  return `${HEADER_SEPARATOR}
Meeting Name: ${session.topic}
Date: ${formattedDate}
Time Started: ${session.startTime}
Participants:
${participantNames}
${HEADER_SEPARATOR}`
}

const generateTranscriptFilename = (session) => {
  const { year, month, day } = formatDateComponents(session.date)
  const dateForFilename = `${year}_${month}_${day}`
  const timeForFilename = session.startTime?.replace(':', '_') || ''

  return `${session.topic}_${dateForFilename}_${timeForFilename}.txt`
}

const setFetchingInProgress = value => ({
  type: RECENTS.FETCHING_IN_PROGRESS,
  payload: value
})

const setHasMore = value => ({
  type: RECENTS.HAS_MORE_RECENTS,
  payload: value
})

const setNextPageStartId = value => ({
  type: RECENTS.NEXT_PAGE_START_ID,
  payload: value
})

const fetchedRecents = recents => ({
  type: RECENTS.FETCHED_RECENTS,
  payload: recents
})

const updateSharingStatus = payload => ({
  type: RECENTS.UPDATE_SHARING_STATUS,
  payload
})

const toggleSharingUpdateInProgress = payload => ({
  type: RECENTS.TOGGLE_SHARING_UPDATE,
  payload
})

const setSummaryFeedbackSubmitted = payload => ({
  type: RECENTS.SET_SUMMARY_FEEDBACK_SUBMITTED,
  payload
})

export const setPlayingRecording = payload => ({
  type: RECENTS.SET_PLAYING_RECORDING,
  payload
})

export const openRecordingVideo = ({ recording, url, sessionId }) => (dispatch, getState) => {
  const isSalesforceSummary = isSalesforceIntegration(getState())

  if (isSalesforceSummary) {
    window.open(`${window.location}&recordingUrl=${url}`)
  } else {
    batch(() => {
      dispatch(setPlayingRecording({ url }))
      dispatch(showRecordingSlideshow({ recording, sessionId }))
    })
  }
}

const extractLinks = phrases =>
  phrases
    .map(phrase => {
      const matches = phrase.content.match(URL_REGEX) || []

      return matches.map(match => ({
        ...phrase,
        url: match.match(/https/i)?.index === 0 ? match : `https://${match}`
      }))
    })
    .flat()
    .map(({ url, name }) => {
      const mapping = LINK_MAPPINGS.find(({ matcher }) => matcher.test(url))
      const type = mapping?.type || LINK_TYPES.LINK
      let fileName = HARDCODED_LINK_TYPES[type]

      if (FILENAME_LINK_TYPES.includes(type)) {
        // will only catch more common cases where filename is at the end
        fileName = decodeURIComponent(url.substring(url.lastIndexOf('/') + 1))
      }

      return {
        fileName,
        sender: name,
        type,
        url
      }
    })

const parseSharingStatus = ({ link, sessionId, shared, sharedBy }) => ({
  link,
  sessionId,
  shared,
  sharedBy
})

const fetchSessionDetails = async (sessionId) => {
  logInfo({ __filename }, 'Retrieving updated recent info')

  const [ai, chat = [], transcriptRaw, polls, sharingStatus] = await Promise.all([
    fetchSessionAI(sessionId),
    fetchSessionChat(sessionId),
    fetchSessionTranscript(sessionId),
    fetchSessionPolls(sessionId),
    fetchSharingStatus(sessionId)
  ])

  const { sessionId: _, ...transcript } = transcriptRaw

  return {
    sessionId,
    resources: {
      actionItems: ai.actionItems,
      chat,
      links: extractLinks(chat),
      summary: ai.summary,
      polls,
      transcript,
      sharingStatus: parseSharingStatus(sharingStatus)
    }
  }
}

const fetchDetailsForSessions = async sessionsMap => {
  const details = await Promise.all(
    Object.keys(sessionsMap).map(key => fetchSessionDetails(key))
  )

  return details.reduce((prev, next) => {
    prev[next.sessionId] = deepmerge(prev[next.sessionId], next)

    return prev
  }, sessionsMap)
}

export const getSessionDetails = sessionId => async dispatch => {
  const [session, sessionDetails] = await Promise.all([
    fetchRecent(sessionId),
    fetchSessionDetails(sessionId)
  ])

  if (!session) {
    logInfo({ __filename, sessionId }, 'Session not found')

    return null
  }

  dispatch(fetchedRecents({
    [sessionId]: deepmerge(
      {
        ...session[sessionId],
        resources: {
          ...session[sessionId].resources,
          links: extractLinks(session[sessionId].resources.chat)
        }
      },
      sessionDetails
    )
  }))

  return sessionDetails
}

export const shareMeetingResources = sessionId => async dispatch => {
  logInfo({ __filename, sessionId }, 'Create sharing link for session')
  dispatch(toggleSharingUpdateInProgress(true))
  const response = await createSharingLink(sessionId)

  if (api.isErrorResponse(response)) {
    logInfo({ __filename }, 'Error creating link ')
    dispatch(toggleSharingUpdateInProgress(false))
    return showErrorNotification(translate('could_not_create_link'))
  }

  dispatch(updateSharingStatus({
    [sessionId]: {
      resources: {
        sharingStatus: parseSharingStatus(response)
      }
    }
  }))
  dispatch(toggleSharingUpdateInProgress(false))
  return response
}

export const copySharingLink = link => async () => {
  logInfo({ __filename, link }, 'Copying shareable link to clipboard')

  try {
    await copy(link)
    showInlineNotification(translate('shared_link_copied'))
  } catch (err) {
    showErrorNotification(translate('share_link_copy_fail'))
  }
}

export const removeSharingLink = sessionId => async dispatch => {
  logInfo({ __filename, sessionId }, 'Removing shareable link for session')
  dispatch(toggleSharingUpdateInProgress(true))
  const response = await deleteSharingLink(sessionId)

  if (api.isErrorResponse(response)) {
    logInfo(
      { __filename, sessionId },
      'Could not remove shareable link for session'
    )
    dispatch(toggleSharingUpdateInProgress(false))
    return showErrorNotification(translate('generic_error'))
  }

  dispatch(
    updateSharingStatus({
      [sessionId]: {
        resources: {
          sharingStatus: {
            shared: false,
            link: undefined,
            sharedBy: undefined
          }
        }
      }
    })
  )
  dispatch(toggleSharingUpdateInProgress(false))
  showInlineNotification(translate('removed_meeting_link'))
}

export const getNextRecentsPage = () => async (dispatch, getState) => {
  const state = getState()
  const hasMore = getHasMore(state)
  const fetchingInProgress = getFetchingInProgress(state)

  if (fetchingInProgress) {
    logInfo({ __filename }, 'Retrieving recents canceled due to fetching already in progress')

    return
  }

  if (!hasMore) {
    logInfo({ __filename, hasMore }, 'Retrieving recents canceled due to reaching the last page')

    return
  }

  dispatch(setFetchingInProgress(true))

  const lastSessionId = getNextPageStartId(state)

  logInfo({ __filename }, 'Retrieving recents')

  const { content, hasMorePages, nextPageStartId } = await fetchRecents({
    maxResults: MAX_RECENTS_RESULTS,
    lastSessionId
  })

  const recents = await fetchDetailsForSessions(content)

  batch(() => {
    dispatch(setNextPageStartId(nextPageStartId))
    dispatch(setHasMore(hasMorePages))
    dispatch(fetchedRecents(recents))
  })
}

export const getLatestRecents = () => async dispatch => {
  logInfo({ __filename }, 'Retrieving latest recents in background')

  const { content } = await fetchRecents({
    maxResults: MAX_RECENTS_RESULTS
  })

  const recents = await fetchDetailsForSessions(content)

  dispatch(fetchedRecents(recents))
}

export const copyShareableLinkToClipboard = recording => async () => {
  const recordingId = recording.id
  const recordingName = recording.fileName

  logInfo({ __filename, recordingId }, 'Copying recording link to clipboard')

  analytics.trackInitEvent(analytics.EVENT_NAMES.COPY_RECORDING_TO_CLIPBOARD, {
    recordingName
  })

  const shareUrl = await fetchRecordingShareLink(recordingId)
  try {
    await copy(shareUrl)
    showInlineNotification(translate('share_link_copied'))
  } catch (err) {
    logInfo({ __filename, err }, 'Failed copying recording link to clipboard')

    analytics.trackFailedEvent(
      analytics.EVENT_NAMES.COPY_RECORDING_TO_CLIPBOARD,
      {
        recordingName,
        reason: err
      }
    )
    showErrorNotification('could_not_copy_share_link')
  }
}

export const showRecordingSlideshow = ({ recording, sessionId }) => dispatch => {
  dispatch(setDialog(DIALOGS.RECORDING_SLIDESHOW, { recording, sessionId }))
}

export const showScreenshotsSlideshow = id => dispatch => {
  dispatch(setDialog(DIALOGS.SCREENSHOTS_SLIDESHOW, { id }))
}

export const showSessionActionItems = id => dispatch => {
  dispatch(setDialog(DIALOGS.ACTION_ITEMS, { id }))
}

export const showSessionChat = id => dispatch => {
  dispatch(setDialog(DIALOGS.CHAT, { id }))
}

export const showSessionLinks = (id, resourceType) => dispatch => {
  dispatch(setDialog(DIALOGS.SHARED_MATERIALS, { id, resourceType }))
}

export const showSessionTranscript = id => dispatch => {
  dispatch(setDialog(DIALOGS.TRANSCRIPTIONS, { id }))
}

export const showSessionSummary = ({ id, showFeedback }) => dispatch => {
  dispatch(setDialog(DIALOGS.SUMMARY, { id, showFeedback }))
}

export const showSessionParticipants = id => dispatch => {
  dispatch(setDialog(DIALOGS.PARTICIPANTS, { id }))
}

export const showSessionPolls = id => dispatch => {
  dispatch(setDialog(DIALOGS.POLLS, { id }))
}

export const retrievePresignedUrl = url => async () => {
  const resp = await fetchRecordingPresignedUrl(url)

  if (api.isErrorResponse(resp)) {
    showErrorNotification(translate('error_fetching_recording_url'))

    logInfo({ __filename, resp }, 'Failed to retrieve presigned url')

    return
  }

  return resp.url
}

export const downloadTranscript = (sessionId, transcriptRef) => async (
  dispatch,
  getState
) => {
  const state = getState()
  const session = getSession(sessionId)(state)
  const filename = `${session.topic}.html`
  return downloadAsHtml(transcriptRef, filename)
}

export const submitSummaryFeedback = (sessionId, liked) => async dispatch => {
  analytics.trackEvent(analytics.EVENT_NAMES.SUMMARY_FEEDBACK, null, {
    liked,
    sessionId
  })

  showInlineNotification(translate('meeting_summary_feedback_submitted'))

  dispatch(setSummaryFeedbackSubmitted({ sessionId }))
}

export const downloadTranscriptAsText = (sessionId) => async (
  dispatch,
  getState
) => {
  const state = getState()
  const phrases = getSessionTranscript(sessionId)(state)
  const session = getSession(sessionId)(state)
  const participants = getSessionParticipants(sessionId)(state)
  const isMobileSummary = getIsMobileSummaryPage(state)

  const filename = generateTranscriptFilename(session)

  const header = generateTranscriptHeader(session, participants)

  const transcriptContent = phrases.reduce((acc, phrase, idx) => {
    if (idx) {
      acc += '\n\n'
    }

    acc += `${phrase.participantName} - ${formatTranscriptTimestamp(phrase.timestamp)}\n`
    acc += phrase.text

    return acc
  }, '')

  const text = `${header}\n\n${transcriptContent}`

  return downloadAsText(text, filename, isMobileSummary)
}
